import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export interface PaginationConfig {
  pageParam?: string;
  perPageParam?: string;
  orderByParam?: string;
  orderDirectionParam?: string;
  totalPagesParam?: string;
  totalItemsParam?: string;
  disableAutoUrlUpdate?: boolean;
}

export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  sortConfig: SortConfig;
}

export const usePagination = (
  config: PaginationConfig = {},
  defaultSort: SortConfig = { field: 'id', direction: 'desc' }
) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Default parameter names
  const {
    pageParam = 'page',
    perPageParam = 'per_page',
    orderByParam = 'order_by',
    orderDirectionParam = 'order_direction',
    totalPagesParam = 'total_pages',
    totalItemsParam = 'total_items',
    disableAutoUrlUpdate = false
  } = config;

  const [paginationState, setPaginationState] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    sortConfig: defaultSort
  });

  // Convert pagination state to query parameters
  const paginationToQueryParams = (
    queryParams: URLSearchParams,
    currentPage: number,
    itemsPerPage: number,
    sortConfig: SortConfig
  ): URLSearchParams => {
    queryParams.set(pageParam, currentPage.toString());
    queryParams.set(perPageParam, itemsPerPage.toString());
    queryParams.set(orderByParam, sortConfig.field);
    queryParams.set(orderDirectionParam, sortConfig.direction);
    return queryParams;
  };

  // Convert query parameters to pagination state
  const queryParamsToPagination = (queryParams: URLSearchParams): Partial<PaginationState> => {
    const currentPage = queryParams.get(pageParam);
    const itemsPerPage = queryParams.get(perPageParam);
    const totalPages = queryParams.get(totalPagesParam);
    const totalItems = queryParams.get(totalItemsParam);
    const orderBy = queryParams.get(orderByParam);
    const orderDirection = queryParams.get(orderDirectionParam) as 'asc' | 'desc';

    const result: Partial<PaginationState> = {};

    if (currentPage) result.currentPage = parseInt(currentPage);
    if (itemsPerPage) result.itemsPerPage = parseInt(itemsPerPage);
    if (totalPages) result.totalPages = parseInt(totalPages);
    if (orderBy && orderDirection) {
      result.sortConfig = { field: orderBy, direction: orderDirection };
    }

    return result;
  };

  // Update pagination state and URL
  const updatePagination = (updates: Partial<PaginationState>) => {
    const newState = { ...paginationState, ...updates };
    setPaginationState(newState);

    // Update URL using the local newState variable
    const queryParams = new URLSearchParams(location.search);
    paginationToQueryParams(queryParams, newState.currentPage, newState.itemsPerPage, newState.sortConfig);
    

    if (!disableAutoUrlUpdate) {
      navigate({
        pathname: location.pathname,
        search: queryParams.toString()
      }, { replace: true });
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= paginationState.totalPages) {
      updatePagination({ currentPage: newPage });
    }
  };

  // Handle sort change
  const handleSort = (field: string) => {
    const newDirection = paginationState.sortConfig.field === field && 
                        paginationState.sortConfig.direction === 'asc' ? 'desc' : 'asc';
    updatePagination({ 
      sortConfig: { field, direction: newDirection },
      currentPage: 1 // Reset to first page when sorting
    });
  };

  // Initialize from URL on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlPagination = queryParamsToPagination(queryParams);
    
    if (Object.keys(urlPagination).length > 0) {
      setPaginationState(prev => ({ ...prev, ...urlPagination }));
    }
  }, [location.search]);

  return {
    paginationState,
    updatePagination,
    handlePageChange,
    handleSort,
    paginationToQueryParams,
    queryParamsToPagination
  };
}; 